import React, { useMemo, useState } from 'react';
import { resolveCommercialState } from './companyPlans';

const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const MODULE_DEFINITIONS = [
  {
    key: 'logistics',
    label: 'Logística',
    description: 'Creás unidades con chofer y transporte. Después armás rutas con destinos.',
    includedHint: 'Módulo principal',
  },
  {
    key: 'service',
    label: 'Servicio',
    description: 'Tomás pedidos, programados y contratos sin mezclar lo comercial con la operación.',
    includedHint: 'Circuito comercial',
  },
  {
    key: 'routeMonitor',
    label: 'Monitor en tiempo real',
    description: 'Te muestra las rutas activas y el estado de cada unidad en movimiento.',
    includedHint: 'Vista en vivo',
  },
  {
    key: 'reports',
    label: 'Reportes y liquidación',
    description: 'Revisás historial, liquidaciones y números generales de la empresa.',
    includedHint: 'Cierre y lectura',
  },
  {
    key: 'stock',
    label: 'Stock y movimientos',
    description: 'Registrás compras, controlás cantidades y preparás salidas.',
    includedHint: 'Control de mercadería',
  },
  {
    key: 'fleetExpenses',
    label: 'Gastos de Flota',
    description: 'Cargás combustible, service y otros gastos de cada transporte.',
    includedHint: 'Costos del transporte',
  },
  {
    key: 'systemGuide',
    label: 'Guía y soporte',
    description: 'Te ubica, te explica qué sigue y te ayuda a moverte sin perder tiempo.',
    includedHint: 'Ayuda activa',
    alwaysOn: true,
  },
];

const QUICK_HELP = [
  {
    id: 'inicio',
    question: '¿Cómo arranco a operar con orden?',
    answer: 'Empezá por Logística. Primero cargá chofer y transporte, después armá una unidad y recién ahí prepará la primera ruta.',
  },
  {
    id: 'modulos',
    question: '¿Qué cambia según el plan?',
    answer: 'El plan define qué módulos tenés habilitados. Siempre arrancás con una base, y después podés sumar más herramientas según cómo trabajás.',
  },
  {
    id: 'empresa',
    question: '¿Qué necesitás resolver en G-CENTRA?',
    answer: 'Usá esta guía para entender qué hace cada módulo, por dónde empezar y qué te conviene abrir según la tarea del día.',
  },
  {
    id: 'evolucion',
    question: '¿Qué hace hoy la guía y soporte?',
    answer: 'La guía y soporte te ubica, te explica qué se hace en cada módulo y te ayuda a pasar al siguiente paso sin llenarte de texto.',
  },
];

const normalizeModules = (companyModules = null) => {
  return MODULE_DEFINITIONS.map((module) => {
    const enabled = module.alwaysOn || companyModules?.[module.key]?.enabled !== false;
    return {
      ...module,
      enabled,
      statusLabel: enabled ? 'Activo' : 'En expansión',
    };
  });
};

const buildFirstSteps = (enabledModuleKeys = new Set(), commercialState = null) => {
  const steps = [
    {
      title: 'Validá tu espacio operativo',
      description: 'Confirmá el nombre de la empresa, el plan activo y qué módulos están habilitados.',
    },
  ];

  if (commercialState?.isExpired) {
    steps.push({
      title: 'Reactivá el servicio',
      description: 'El entorno quedó pausado por vencimiento. Definí el plan para volver a operar sin cortes.',
    });
  } else if (commercialState?.isExpiringSoon) {
    steps.push({
      title: 'Revisá la vigencia comercial',
      description: 'El entorno sigue activo, pero conviene definir el próximo paso antes del vencimiento o renovación.',
    });
  }

  if (enabledModuleKeys.has('logistics')) {
    steps.push({
      title: 'Prepará la estructura diaria',
      description: 'Cargá choferes y transportes para empezar a armar unidades de trabajo.',
    });
  }

  if (enabledModuleKeys.has('service')) {
    steps.push({
      title: 'Ordená pedidos y contratos',
      description: 'Usá Servicio para tomar pedidos, revisar programados y administrar contratos sin perder trazabilidad.',
    });
  }

  if (enabledModuleKeys.has('routeMonitor')) {
    steps.push({
      title: 'Activá lectura visual del movimiento',
      description: 'Usá Monitor en tiempo real para seguir rutas activas y ver qué está pasando en cada unidad.',
    });
  }

  if (enabledModuleKeys.has('reports')) {
    steps.push({
      title: 'Cerrá el ciclo con lectura ejecutiva',
      description: 'Usá Reportes y liquidación para revisar historial, cierres y números generales.',
    });
  }

  if (enabledModuleKeys.has('stock')) {
    steps.push({
      title: 'Ordená control interno y stock',
      description: 'Usá Stock para registrar compras, controlar cantidades y preparar salidas.',
    });
  }

  return steps.slice(0, 4);
};

const StatCard = ({ title, value, subtitle, accent = 'cyan' }) => {
  const accents = {
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-950',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-950',
    violet: 'border-violet-200 bg-violet-50 text-violet-950',
    amber: 'border-amber-200 bg-amber-50 text-amber-950',
    rose: 'border-rose-200 bg-rose-50 text-rose-950',
  };

  return (
    <div className={`rounded-3xl border p-5 shadow-sm ${accents[accent] || accents.cyan}`}>
      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">{title}</p>
      <p className="mt-3 text-2xl font-black text-slate-950">{value}</p>
      <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
    </div>
  );
};

const getToneAccent = (tone = 'emerald') => {
  if (tone === 'red') return 'rose';
  if (tone === 'amber') return 'amber';
  if (tone === 'cyan') return 'cyan';
  if (tone === 'violet') return 'violet';
  return 'emerald';
};

function SystemGuide({
  onBackToMenu,
  companyId,
  companyName,
  companyModules,
  companyGeneralSettings,
  companyPlanSettings,
  commercialState,
}) {
  const [openHelp, setOpenHelp] = useState(QUICK_HELP[0].id);

  const modules = useMemo(() => normalizeModules(companyModules), [companyModules]);
  const enabledModules = useMemo(() => modules.filter((module) => module.enabled), [modules]);
  const enabledModuleKeys = useMemo(() => new Set(enabledModules.map((module) => module.key)), [enabledModules]);

  const resolvedCommercialState = useMemo(() => {
    return commercialState || resolveCommercialState({
      generalSettings: companyGeneralSettings || {},
      planSettings: companyPlanSettings || {},
      modulesSettings: companyModules || {},
    });
  }, [commercialState, companyGeneralSettings, companyModules, companyPlanSettings]);

  const firstSteps = useMemo(
    () => buildFirstSteps(enabledModuleKeys, resolvedCommercialState),
    [enabledModuleKeys, resolvedCommercialState]
  );

  const planLabel = resolvedCommercialState?.planMeta?.label || 'Plan actual';
  const accent = getToneAccent(resolvedCommercialState?.tone);
  const countdownLabel = resolvedCommercialState?.countdownLabel || '';
  const nextDateLabel = resolvedCommercialState?.nextDateLabel || '';
  const billingLabel = nextDateLabel || (countdownLabel ? `Quedan ${countdownLabel}` : 'Sin vencimiento visible');

  return (
    <div className="min-h-full bg-slate-100 p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <section className="overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-7 text-white shadow-2xl">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200">
                <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="w-4 h-4" />
                Guía y soporte
              </div>
              <h1 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">Guía de uso y evolución</h1>
              <p className="mt-3 max-w-3xl text-sm text-slate-300 md:text-base">
                Este espacio te ayuda a entender qué módulos tenés activos, qué hace cada uno y por dónde conviene empezar hoy.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">Empresa: {companyName}</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">Plan: {planLabel}</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">Módulos activos: {enabledModules.length}</span>
              </div>
            </div>

            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="inline-flex items-center gap-2 self-start rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-white/10"
              >
                <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="w-5 h-5" />
                Volver al inicio
              </button>
            )}
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Empresa activa" value={companyName} subtitle="Este es el espacio de trabajo de la empresa." accent="cyan" />
          <StatCard title="Plan actual" value={planLabel} subtitle="Marca qué módulos están disponibles hoy." accent={accent} />
          <StatCard title="Módulos activos" value={enabledModules.length} subtitle="Herramientas que hoy podés usar en la empresa." accent="emerald" />
          <StatCard title="Acceso" value={companyId || 'empresa'} subtitle="Identificador interno del espacio actual." accent="amber" />
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-cyan-500/10 p-3 text-cyan-600">
                <Icon path="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900">Herramientas disponibles</h2>
                <p className="text-sm text-slate-500">Qué hace cada módulo y en qué momento te conviene usarlo.</p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {modules.map((module) => (
                <div
                  key={module.key}
                  className={`rounded-3xl border p-5 transition-all ${module.enabled ? 'border-slate-200 bg-slate-50' : 'border-dashed border-slate-200 bg-slate-50/70 opacity-85'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-black text-slate-900">{module.label}</h3>
                      <p className="mt-2 text-sm text-slate-600">{module.description}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${module.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {module.statusLabel}
                    </span>
                  </div>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{module.includedHint}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-violet-500/10 p-3 text-violet-600">
                <Icon path="M11.25 6.75v4.5m0 0v4.5m0-4.5h4.5m-4.5 0h-4.5" className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900">Primeros pasos</h2>
                <p className="text-sm text-slate-500">Orden simple para empezar a trabajar sin mezclar pasos.</p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {firstSteps.map((step, index) => (
                <div key={step.title} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-sm font-black text-white">{index + 1}</div>
                    <div>
                      <h3 className="text-base font-black text-slate-900">{step.title}</h3>
                      <p className="mt-2 text-sm text-slate-600">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-amber-500/10 p-3 text-amber-600">
                <Icon path="M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5" className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900">Plan y alcance</h2>
                <p className="text-sm text-slate-500">Qué tenés habilitado hoy y cómo crece la empresa según el plan.</p>
              </div>
            </div>

            <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] ${
                  accent === 'rose' ? 'bg-rose-100 text-rose-700' :
                  accent === 'amber' ? 'bg-amber-100 text-amber-700' :
                  accent === 'cyan' ? 'bg-cyan-100 text-cyan-700' :
                  accent === 'violet' ? 'bg-violet-100 text-violet-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {resolvedCommercialState?.badge || 'Plan activo'}
                </span>
                {countdownLabel ? (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-slate-600">
                    {countdownLabel}
                  </span>
                ) : null}
              </div>

              <p className="mt-4 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Plan activo</p>
              <p className="mt-3 text-3xl font-black text-slate-900">{planLabel}</p>
              <p className="mt-3 text-sm text-slate-600">
                {resolvedCommercialState?.description || 'La empresa trabaja hoy con los módulos que marca su plan actual.'}
              </p>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Estado comercial</p>
                  <p className="mt-2 text-base font-black text-slate-900">{nextDateLabel || resolvedCommercialState?.badge || 'Plan activo'}</p>
                  <p className="mt-1 text-sm text-slate-600">{countdownLabel ? `Quedan ${countdownLabel}` : billingLabel}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Módulos activos</p>
                  <p className="mt-2 text-base font-black text-slate-900">{enabledModules.length}</p>
                  <p className="mt-1 text-sm text-slate-600">Cantidad de módulos disponibles hoy.</p>
                </div>
              </div>
            </div>

            <div className="mt-5 space-y-3 text-sm text-slate-600">
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4">
                <Icon path="M4.5 12.75l6 6 9-13.5" className="mt-0.5 w-5 h-5 text-emerald-500" />
                <span>Tu plan define qué módulos podés usar hoy.</span>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4">
                <Icon path="M4.5 12.75l6 6 9-13.5" className="mt-0.5 w-5 h-5 text-emerald-500" />
                <span>La guía y soporte te ayuda a ubicarte y seguir sin perder tiempo.</span>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4">
                <Icon path="M4.5 12.75l6 6 9-13.5" className="mt-0.5 w-5 h-5 text-emerald-500" />
                <span>Podés sumar más módulos sin tener que cambiar la lógica de trabajo.</span>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-slate-900 p-3 text-white">
                <Icon path="M3 3.75A.75.75 0 013.75 3h10.5a.75.75 0 01.53.22l3 3a.75.75 0 01.22.53v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V3.75z" className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900">Evolución del producto</h2>
                <p className="text-sm text-slate-500">Lo que ya podés usar hoy y lo que se puede sumar después.</p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-600">Disponible ahora</p>
                <ul className="mt-4 space-y-2 text-sm text-emerald-900">
                  <li>Acceso por empresa</li>
                  <li>Trabajo por módulos</li>
                  <li>Lectura comercial sincronizada</li>
                </ul>
              </div>
              <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-600">En mejora</p>
                <ul className="mt-4 space-y-2 text-sm text-amber-900">
                  <li>Ayuda más clara por módulo</li>
                  <li>Mejor lectura de reportes</li>
                  <li>Guías más cortas por tarea</li>
                </ul>
              </div>
              <div className="rounded-3xl border border-cyan-200 bg-cyan-50 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-700">Próximamente</p>
                <ul className="mt-4 space-y-2 text-sm text-cyan-900">
                  <li>Más profundidad en Stock</li>
                  <li>Más lectura en vivo del Monitor</li>
                  <li>Nuevas mejoras por plan</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-rose-500/10 p-3 text-rose-600">
              <Icon path="M8.25 9.75h7.5m-7.5 3h4.5M3.75 19.5h16.5A1.5 1.5 0 0021.75 18V6A1.5 1.5 0 0020.25 4.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900">Ayuda rápida</h2>
              <p className="text-sm text-slate-500">Respuestas cortas para entender la app sin salir de donde estás.</p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {QUICK_HELP.map((item) => {
              const isOpen = openHelp === item.id;
              return (
                <div key={item.id} className="overflow-hidden rounded-3xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setOpenHelp(isOpen ? null : item.id)}
                    className="flex w-full items-center justify-between gap-3 bg-white px-5 py-4 text-left transition-colors hover:bg-slate-50"
                  >
                    <span className="text-sm font-black text-slate-900 md:text-base">{item.question}</span>
                    <Icon path={isOpen ? 'M19.5 12h-15' : 'M12 4.5v15m7.5-7.5h-15'} className="w-5 h-5 text-slate-500" />
                  </button>
                  {isOpen ? <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">{item.answer}</div> : null}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

export default SystemGuide;
